<template>
  <section class="p-4">
    <div class="top-bar flex justify-end items-center gap-2 mb-4">
      <button
        v-if="isTakeoffMode"
        type="button"
        class="boq-takeoff-btn"
        @click="showTakeoffImportModal = true"
      >
        <b-icon pack="fas" icon="tag" size="is-small" />
        <span class="boq-takeoff-btn__label">
          <span>{{ $t('load') }}</span>
          <span>Takeoff</span>
        </span>
      </button>
      <v-button
        v-if="isTakeoffMode && !hasTakeoffRoot"
        icon="plus"
        :loading="creatingTakeoff"
        @click="createTakeoffRoot"
      >
        {{ $t('create_takeoff') }}
      </v-button>
      <v-button icon="plus" @click="openCreateLink">
        {{ $t('add_wbs_budget_link') }}
      </v-button>
    </div>
    <div class="overflow-x-auto bg-white panel">
      <table class="w-full min-w-max text-xs border border-gray-300 border-collapse">
        <thead>
          <tr class="bg-blue">
            <th class="w-8 min-w-[2rem] px-2 py-2 font-bold text-white text-center uppercase border border-blue-lighter" />
            <th class="px-2 py-2 font-bold text-white text-center uppercase whitespace-nowrap border border-blue-lighter">Código</th>
            <th class="min-w-[16rem] px-2 py-2 font-bold text-white text-left uppercase whitespace-nowrap border border-blue-lighter">Nombre de la medición</th>
            <th class="px-2 py-2 font-bold text-white text-center uppercase whitespace-nowrap border border-blue-lighter">Ud.</th>
            <th class="px-2 py-2 font-bold text-white text-center uppercase whitespace-nowrap border border-blue-lighter">Total</th>
            <th class="px-2 py-2 font-bold text-white text-center uppercase whitespace-nowrap border border-blue-lighter">Asignado</th>
            <th class="px-2 py-2 font-bold text-white text-center uppercase whitespace-nowrap border border-blue-lighter">Pendiente</th>
            <!-- lo comentamos por ahora por si se requiere en un futuro -->
            <!-- <th class="min-w-[6rem] px-2 py-2 font-bold text-white text-center uppercase whitespace-nowrap border border-blue-lighter">Progreso</th> -->
            <!-- <th class="px-2 py-2 font-bold text-white text-center uppercase whitespace-nowrap border border-blue-lighter">Presup.</th> -->
            <!-- <th class="px-2 py-2 font-bold text-white text-center uppercase whitespace-nowrap border border-blue-lighter">Valor ganado</th> -->
            <th class="w-12 px-2 py-2 font-bold text-white text-center uppercase whitespace-nowrap border border-blue-lighter">Subs</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="8" class="px-4 py-6 text-center text-gray-500">
              {{ $t('loading') }}...
            </td>
          </tr>
          <tr v-else-if="!groups.length">
            <td colspan="8" class="px-4 py-6 text-center text-gray-500">
              {{ $t('no_results') }}
            </td>
          </tr>
          <template v-for="group in groups">
            <tr
              :key="group.id"
              class="cursor-pointer"
              :class="groupRowClass(group)"
              @click="toggleGroup(group.id)"
            >
              <td class="px-2 py-2 border border-gray-300" colspan="8">
                <div class="flex items-center gap-2 min-w-0">
                  <button
                    type="button"
                    class="inline-flex flex-shrink-0 items-center justify-center w-6 h-6 bg-transparent border-0 cursor-pointer focus:outline-none"
                    :class="groupHeaderChevronClass(group)"
                    :aria-expanded="isGroupExpanded(group.id)"
                    @click.stop="toggleGroup(group.id)"
                  >
                    <b-icon
                      pack="fas"
                      :icon="isGroupExpanded(group.id) ? 'chevron-down' : 'chevron-right'"
                      size="is-small"
                    />
                  </button>
                  <span
                    class="flex-shrink-0 px-2 py-0.5 text-[0.65rem] font-bold text-white rounded"
                    :class="groupHeaderBadgeClass(group)"
                  >{{ group.badgeLabel || group.code }}</span>
                  <span
                    class="flex-1 min-w-0 overflow-hidden font-semibold truncate"
                    :class="groupHeaderTitleClass(group)"
                  >{{ group.title }}</span>
                  <span
                    class="flex-shrink-0"
                    :class="groupHeaderMetaClass(group)"
                  >{{ group.measurementsLabel }}</span>
                  <button
                    v-if="group.canAddChild"
                    type="button"
                    class="inline-flex flex-shrink-0 items-center justify-center w-6 h-6 text-white border-0 rounded-full cursor-pointer focus:outline-none"
                    :class="groupHeaderActionClass(group)"
                    :title="group.addChildTitle"
                    :aria-label="group.addChildTitle"
                    @click.stop="openCreateResource(group)"
                  >
                    <b-icon pack="fas" icon="plus" size="is-small" />
                  </button>
                  <span
                    class="inline-flex flex-shrink-0 items-center justify-center min-w-[1.5rem] h-6 px-1.5 text-[0.65rem] font-bold text-white rounded-full"
                    :class="groupHeaderCountClass(group)"
                  >{{ group.count }}</span>
                </div>
              </td>
            </tr>

            <boq-tree-items
              :key="`tree-items-${group.id}-${treeRefreshToken(group.id)}`"
              v-if="isGroupExpanded(group.id)"
              :project-id="projectId"
              :parent-boq-root-id="group.id"
              :root-context="group.rootContext"
              :depth="1"
              :refresh-token="treeRefreshToken(group.id)"
            />
          </template>
        </tbody>
      </table>
    </div>

    <boq-create-link-modal
      ref="createLinkModal"
      :project-id="projectId"
      @created="handleLinkCreated"
    />

    <boq-create-resource-modal
      ref="createResourceModal"
      :project-id="projectId"
      @created="handleResourceCreated"
      @updated="handleResourceUpdated"
    />

    <boq-import-takeoff-modal
      v-model="showTakeoffImportModal"
      :project-id="projectId"
      @success="handleTakeoffImported"
    />
  </section>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import BoqCreateLinkModal from './boq-create-link-modal.vue'
import BoqCreateResourceModal from './boq-create-resource-modal.vue'
import BoqImportTakeoffModal from './boq-import-takeoff-modal.vue'
import BoqTreeItems from './boq-tree-items.vue'

const BOQ_SOURCE_IMPORTED = 'primavera_p6_imported'
const BOQ_SOURCE_MANUAL = 'primavera_p6_manual'
const TYPE_CERTIFIER_HEAD = 'App\\Models\\AssignmentType\\CertifierHead'
/** Mismos valores que BoqRoot::ROW_KINDS */
const ROW_KIND_TAKEOFF = 0
const ROW_KIND_DISCIPLINE = 1
const ROW_KIND_STAGE = 2

export default {
  name: 'boq-root',

  components: {
    BoqCreateLinkModal,
    BoqCreateResourceModal,
    BoqImportTakeoffModal,
    BoqTreeItems,
  },

  provide() {
    return {
      boqTreeContext: this,
    }
  },

  props: {
    projectId: { type: [String, Number], required: true },
  },

  data() {
    return {
      loading: false,
      creatingTakeoff: false,
      showTakeoffImportModal: false,
      expandedGroupIds: [],
      expandedItemIds: [],
      itemLinkForms: {},
      groups: [],
      parentActivities: [],
      isActivitiesLoading: false,
      linkingItemIds: [],
      unlinkingKeys: [],
      deletingTakeoffIds: [],
      treeRefreshTokens: {},
      resourceLinkDetails: {},
      resourceAssignmentDetails: {},
      resourceLinksLoading: [],
      activitySearchLoadingIds: [],
      activitySearchTimeouts: {},
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    isTakeoffMode() {
      const configs = this.project && this.project.configs
      return !!(configs && configs.progress_type_mode === 'takeoff')
    },

    hasTakeoffRoot() {
      return this.groups.some((group) => group.isTakeoffRoot)
    },
  },

  created() {
    this.loadBoqRoots()
    this.loadParentActivities()
  },

  methods: {
    rowKindOf(node) {
      if (!node || node.row_kind == null || node.row_kind === '') {
        return null
      }
      return Number(node.row_kind)
    },

    isTakeoffRowKind(rowKind) {
      return rowKind === ROW_KIND_TAKEOFF
        || rowKind === ROW_KIND_DISCIPLINE
        || rowKind === ROW_KIND_STAGE
    },

    takeoffBadgeLabel(rowKind) {
      if (rowKind === ROW_KIND_TAKEOFF) {
        return 'TKF'
      }
      if (rowKind === ROW_KIND_DISCIPLINE) {
        return 'DISC'
      }
      if (rowKind === ROW_KIND_STAGE) {
        return 'STAGE'
      }
      return ''
    },

    async loadBoqRoots() {
      this.loading = true
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/boq-roots`
        )
        if (data && data.success) {
          this.groups = (data.boq_roots || []).map((root) => this.mapGroup(root))
        } else {
          this.groups = []
        }
      } catch (error) {
        this.groups = []
        this.$notify.error('error_loading_data')
      } finally {
        this.loading = false
      }
    },

    mapGroup(root) {
      const rowKind = this.rowKindOf(root)
      const isTakeoff = rowKind === ROW_KIND_TAKEOFF
      const isManual = root.code === BOQ_SOURCE_MANUAL
      const isImported = root.code === BOQ_SOURCE_IMPORTED
      const rootContext = {
        isManualRoot: isManual,
        isImportedRoot: isImported,
        isTakeoffRoot: isTakeoff,
        rootCode: root.code,
        rowKind,
      }
      const childCount = Number(root.children_count) || 0

      return {
        id: root.id,
        code: root.code,
        title: isTakeoff ? (root.name || this.$t('takeoff')) : root.name,
        type: root.type,
        rowKind,
        badgeLabel: isTakeoff ? this.takeoffBadgeLabel(rowKind) : root.code,
        rootCode: root.code,
        isManualRoot: isManual,
        isImportedRoot: isImported,
        isTakeoffRoot: isTakeoff,
        isP6Root: isManual || isImported,
        canAddChild: true,
        addChildTitle: isTakeoff
          ? this.$t('add_boq_takeoff_child')
          : (isManual ? this.$t('add_boq_child') : this.$t('add_boq_resource')),
        measurementsLabel: isTakeoff
          ? `${childCount} ${this.$t('tag')}s`
          : `${childCount} ${this.$t('measurements').toLowerCase()}`,
        count: childCount,
        rootContext,
        _raw: root,
      }
    },

    groupRowClass(group) {
      if (group.isTakeoffRoot) {
        return 'boq-takeoff-row'
      }
      if (group.isManualRoot) {
        return 'boq-manual-row'
      }
      return 'bg-blue-lightest hover:bg-blue-lighter'
    },

    groupHeaderChevronClass(group) {
      if (group.isTakeoffRoot) {
        return 'boq-takeoff-row__chevron'
      }
      if (group.isManualRoot) {
        return 'boq-manual-row__chevron'
      }
      return 'text-blue-dark'
    },

    groupHeaderBadgeClass(group) {
      if (group.isTakeoffRoot) {
        return 'boq-takeoff-row__code-badge'
      }
      if (group.isManualRoot) {
        return 'boq-manual-row__code-badge'
      }
      return 'bg-blue'
    },

    groupHeaderTitleClass(group) {
      if (group.isTakeoffRoot) {
        return 'boq-takeoff-row__title'
      }
      if (group.isManualRoot) {
        return 'boq-manual-row__title'
      }
      return 'text-blue-dark'
    },

    groupHeaderMetaClass(group) {
      if (group.isTakeoffRoot) {
        return 'boq-takeoff-row__meta'
      }
      if (group.isManualRoot) {
        return 'boq-manual-row__meta'
      }
      return 'text-gray-500'
    },

    groupHeaderActionClass(group) {
      if (group.isTakeoffRoot) {
        return 'boq-takeoff-row__action-btn'
      }
      if (group.isManualRoot) {
        return 'boq-manual-row__action-btn'
      }
      return 'bg-blue hover:bg-blue-dark'
    },

    groupHeaderCountClass(group) {
      if (group.isTakeoffRoot) {
        return 'boq-takeoff-row__count'
      }
      if (group.isManualRoot) {
        return 'boq-manual-row__count'
      }
      return 'bg-blue-darkest'
    },

    isCertifierHead(node) {
      return node && node.type === TYPE_CERTIFIER_HEAD
    },

    mapGroupItem(child, rootContext, depth = 0) {
      const rowKind = this.rowKindOf(child)
      if (rootContext.isTakeoffRoot || this.isTakeoffRowKind(rowKind)) {
        return this.mapTakeoffItem(child, rootContext, depth)
      }

      const manualTree = rootContext.isManualRoot === true

      if (manualTree && this.isCertifierHead(child)) {
        return this.mapCertifierHeadItem(child, rootContext, depth)
      }

      return {
        ...this.mapResourceItem(child),
        nodeType: 'resource',
        depth,
        isManualTree: manualTree,
      }
    },

    mapTakeoffItem(node, rootContext, depth = 0) {
      const rowKind = this.rowKindOf(node)
      const childCount = Number(node.children_count) || 0
      const assignmentsCount = Number(node.assignments_count) || 0
      const isStage = rowKind === ROW_KIND_STAGE
      // Solo los stages se vinculan a assignments (mediciones/TAGs).
      const displayCount = isStage ? assignmentsCount : childCount
      const canAddChild = rowKind === ROW_KIND_TAKEOFF || rowKind === ROW_KIND_DISCIPLINE

      return {
        nodeType: 'takeoff_node',
        id: node.id,
        code: node.code,
        title: node.name,
        rowKind,
        badgeLabel: this.takeoffBadgeLabel(rowKind),
        depth,
        measurementsLabel: `${displayCount} ${this.$t('tag')}s`,
        // count / childrenCount: el expand usa solo hijos BOQ; stages no expanden.
        count: displayCount,
        childrenCount: childCount,
        canAddChild,
        addChildTitle: this.$t('add_boq_takeoff_child'),
        isTakeoffTree: true,
        parentBoqRootId: node.parent_id,
        rootContext: {
          ...rootContext,
          isTakeoffRoot: true,
        },
        _raw: node,
      }
    },

    mapCertifierHeadItem(node, rootContext, depth = 0) {
      const childCount = Number(node.children_count) || 0

      return {
        nodeType: 'certifier_head',
        id: node.id,
        code: node.code,
        title: node.name,
        depth,
        measurementsLabel: `${childCount} ${this.$t('measurements').toLowerCase()}`,
        count: childCount,
        isManualTree: rootContext.isManualRoot === true,
        rootContext,
        _raw: node,
      }
    },

    mapResourceItem(child) {
      const total = parseFloat(child.total_quantity) || 0
      const unit = child.measure_unit ? child.measure_unit.name : ''
      const totalReserved = parseFloat(child.reserved_quantity) || 0
      const hasTotalCap = child.total_quantity != null && child.total_quantity !== ''
      const availableQuantity = hasTotalCap
        ? Math.max(0, total - totalReserved)
        : null
      const assigned = totalReserved
      const pending = hasTotalCap ? total - assigned : 0
      const progress = hasTotalCap && total > 0
        ? Math.min(100, Math.round((assigned / total) * 100))
        : 0

      return {
        id: child.id,
        code: child.code,
        name: child.name,
        unit,
        total,
        assigned,
        pending,
        progress,
        budget: 0,
        earned: 0,
        linkedActivities: [],
        linkedActivityIds: [],
        activitiesCount: Number(child.activities_count) || 0,
        totalReserved,
        availableQuantity,
        parentBoqRootId: child.parent_id,
        _raw: child,
      }
    },

    async createTakeoffRoot() {
      if (this.creatingTakeoff || this.hasTakeoffRoot) {
        return
      }

      this.creatingTakeoff = true
      try {
        const { data } = await axios.post(
          `/api/v2/project/${this.projectId}/boq-roots`,
          {
            code: 'takeoff',
            name: 'takeoff',
            row_kind: ROW_KIND_TAKEOFF,
          }
        )

        if (data && data.success) {
          this.$notify.success('save_successful')
          this.loadBoqRoots()
          return
        }

        const msg = data.message || 'assignment_change_fail'
        this.$notify.error(this.$t(msg))
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.message)
          || 'assignment_change_fail'
        this.$notify.error(this.$t(msg))
      } finally {
        this.creatingTakeoff = false
      }
    },

    async loadParentActivities() {
      if (!this.projectId) {
        this.parentActivities = []
        return
      }

      this.isActivitiesLoading = true
      try {
        const { data } = await axios.get('/api/v2/activities', {
          params: {
            p: this.projectId,
            per_page: 2000,
            page: 1,
            order_field: 'planning_code',
            order_dir: 'asc',
            light: 1,
          },
        })
        this.parentActivities = (data && Array.isArray(data.activities)) ? data.activities : []
      } catch (error) {
        this.parentActivities = []
        this.$notify.error('error_loading_data')
      } finally {
        this.isActivitiesLoading = false
      }
    },

    activityLabel(activity) {
      if (!activity) {
        return ''
      }
      const code = activity.planning_code ? `${activity.planning_code} - ` : ''
      return code + (activity.name || `#${activity.id}`)
    },

    buildActivityOption(activity) {
      return {
        ...activity,
        label: this.activityLabel(activity),
      }
    },

    getLinkedActivityIds(item) {
      const detail = this.getResourceLinkDetail(item)
      return detail.linkedActivityIds || item.linkedActivityIds || []
    },

    getActivityAutocompleteOptions(item) {
      const form = this.getItemLinkForm(item.id)
      const query = (form.activitySearch || '').trim().toLowerCase()
      const localOptions = this.filterActivityOptions(this.selectableActivities(item), query)

      if (!form.remoteActivityOptions || query.length < 2) {
        return localOptions
      }

      const byId = new Map()
      localOptions.forEach((activity) => byId.set(activity.id, activity))
      form.remoteActivityOptions.forEach((activity) => byId.set(activity.id, activity))

      return Array.from(byId.values())
    },

    filterActivityOptions(activities, query) {
      const options = activities.map((activity) => this.buildActivityOption(activity))
      if (!query) {
        return options
      }

      return options.filter((activity) => {
        const name = (activity.name || '').toLowerCase()
        const code = (activity.planning_code || '').toLowerCase()
        const label = (activity.label || '').toLowerCase()
        return name.includes(query) || code.includes(query) || label.includes(query)
      })
    },

    isActivitySearchLoading(itemId) {
      return this.activitySearchLoadingIds.includes(itemId)
    },

    onActivityAutocompleteFocus(item) {
      const form = this.getItemLinkForm(item.id)
      if (form.remoteActivityOptions) {
        this.$set(form, 'remoteActivityOptions', null)
      }
    },

    onActivityAutocompleteTyping(item, value) {
      const form = this.getItemLinkForm(item.id)
      const text = value != null ? String(value) : ''

      if (form.activityId != null) {
        const selected = this.parentActivities.find((activity) => activity.id === form.activityId)
        const selectedLabel = selected ? this.activityLabel(selected) : ''
        if (text !== selectedLabel) {
          form.activityId = null
        }
      }

      this.$set(form, 'remoteActivityOptions', null)
      this.queueActivitySearch(item, text)
    },

    queueActivitySearch(item, query) {
      const itemId = item.id
      if (this.activitySearchTimeouts[itemId]) {
        clearTimeout(this.activitySearchTimeouts[itemId])
      }

      const trimmed = (query || '').trim()
      if (trimmed.length < 2) {
        return
      }

      this.activitySearchTimeouts[itemId] = setTimeout(() => {
        this.fetchActivitySearchOptions(item, trimmed)
      }, 300)
    },

    async fetchActivitySearchOptions(item, query) {
      const form = this.getItemLinkForm(item.id)
      const itemId = item.id

      if ((form.activitySearch || '').trim() !== query) {
        return
      }

      this.activitySearchLoadingIds = [...this.activitySearchLoadingIds, itemId]

      try {
        const requests = [
          axios.get('/api/v2/activities', {
            params: {
              p: this.projectId,
              n: query,
              per_page: 100,
              page: 1,
              order_field: 'planning_code',
              order_dir: 'asc',
              light: 1,
            },
          }),
          axios.get('/api/v2/activities', {
            params: {
              p: this.projectId,
              filter_planning_code: query,
              per_page: 100,
              page: 1,
              order_field: 'planning_code',
              order_dir: 'asc',
              light: 1,
            },
          }),
        ]

        const [byNameRes, byCodeRes] = await Promise.all(requests)

        if ((form.activitySearch || '').trim() !== query) {
          return
        }

        const linkedIds = this.getLinkedActivityIds(item)
        const merged = []
        const seenIds = new Set()

        ;[byNameRes.data, byCodeRes.data].forEach((data) => {
          const activities = (data && Array.isArray(data.activities)) ? data.activities : []
          activities.forEach((activity) => {
            if (linkedIds.includes(activity.id) || seenIds.has(activity.id)) {
              return
            }
            seenIds.add(activity.id)
            merged.push(activity)
          })
        })

        const options = merged.map((activity) => this.buildActivityOption(activity))

        this.$set(form, 'remoteActivityOptions', options)
      } catch (error) {
        // Sin resultados remotos: se mantiene el filtro local.
      } finally {
        this.activitySearchLoadingIds = this.activitySearchLoadingIds.filter((id) => id !== itemId)
      }
    },

    onActivitySelected(item, activity) {
      const form = this.getItemLinkForm(item.id)

      if (!activity) {
        form.activityId = null
        form.activitySearch = ''
        this.$set(form, 'remoteActivityOptions', null)
        return
      }

      form.activityId = activity.id
      form.activitySearch = activity.label || this.activityLabel(activity)
      this.$set(form, 'remoteActivityOptions', null)
    },

    openCreateLink() {
      this.$refs.createLinkModal.show()
    },

    openCreateResource(node) {
      const payload = node && node._raw ? node._raw : node
      const isManualTree = node.isManualRoot === true
        || node.isManualTree === true
        || (payload && payload.code === BOQ_SOURCE_MANUAL)
      const isTakeoffTree = node.isTakeoffRoot === true
        || node.isTakeoffTree === true
        || (node.rootContext && node.rootContext.isTakeoffRoot === true)
        || this.isTakeoffRowKind(this.rowKindOf(payload))

      this.$refs.createResourceModal.show(payload, {
        isManualTree,
        isTakeoffTree,
        parentRowKind: this.rowKindOf(payload) != null
          ? this.rowKindOf(payload)
          : (node.rowKind != null ? node.rowKind : null),
      })
    },

    openEditTakeoffChild(node) {
      const payload = node && node._raw ? node._raw : node
      // depth 1 bajo la raíz takeoff = disciplina/stage hijo directo
      const parentRowKind = node.depth === 1
        ? ROW_KIND_TAKEOFF
        : ROW_KIND_DISCIPLINE

      this.$refs.createResourceModal.show(payload, {
        edit: true,
        isTakeoffTree: true,
        parentRowKind,
      })
    },

    isDeletingTakeoffChild(id) {
      return this.deletingTakeoffIds.includes(Number(id))
    },

    confirmDeleteTakeoffChild(node) {
      if (!node || this.isDeletingTakeoffChild(node.id)) {
        return
      }
      const name = node.title || node.name || node.code || ''
      this.$buefy.dialog.confirm({
        title: this.$t('delete'),
        message: this.$t('confirm_delete_boq_takeoff_child', [name]),
        type: 'is-danger',
        hasIcon: true,
        icon: 'trash-alt',
        iconPack: 'fas',
        confirmText: this.$t('delete'),
        cancelText: this.$t('cancel'),
        onConfirm: () => {
          this.deleteTakeoffChild(node)
        },
      })
    },

    async deleteTakeoffChild(node) {
      if (!node || !this.projectId || this.isDeletingTakeoffChild(node.id)) {
        return
      }
      const id = Number(node.id)
      this.deletingTakeoffIds = [...this.deletingTakeoffIds, id]
      try {
        const { data } = await axios.delete(
          `/api/v2/project/${this.projectId}/boq-roots/${id}`
        )
        if (data && data.success) {
          this.$notify.success('deleted_successfully')
          const parentId = node.parentBoqRootId
            || (node._raw && node._raw.parent_id)
            || null
          if (parentId) {
            this.bumpTreeRefresh(parentId)
          }
          this.loadBoqRoots()
          return
        }
        const msg = (data && data.message) || 'error'
        this.$notify.error(msg)
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.message)
          || 'error'
        this.$notify.error(msg)
      } finally {
        this.deletingTakeoffIds = this.deletingTakeoffIds.filter((itemId) => itemId !== id)
      }
    },

    handleLinkCreated() {
      this.loadBoqRoots()
      this.$emit('link-created')
    },

    handleResourceCreated(boqRoot) {
      this.loadBoqRoots()
      if (boqRoot && boqRoot.parent_id) {
        this.bumpTreeRefresh(boqRoot.parent_id)
      }
    },

    handleResourceUpdated(boqRoot) {
      this.loadBoqRoots()
      if (boqRoot && boqRoot.parent_id) {
        this.bumpTreeRefresh(boqRoot.parent_id)
      }
    },

    handleTakeoffImported() {
      this.loadBoqRoots()
    },

    treeRefreshToken(parentId) {
      return this.treeRefreshTokens[String(parentId)] || 0
    },

    bumpTreeRefresh(parentId) {
      const key = String(parentId)
      this.treeRefreshTokens = {
        ...this.treeRefreshTokens,
        [key]: (this.treeRefreshTokens[key] || 0) + 1,
      }
    },

    isGroupExpanded(groupId) {
      return this.expandedGroupIds.includes(groupId)
    },

    toggleGroup(groupId) {
      if (this.isGroupExpanded(groupId)) {
        this.expandedGroupIds = this.expandedGroupIds.filter((id) => id !== groupId)
        return
      }
      this.expandedGroupIds = [...this.expandedGroupIds, groupId]
    },

    isItemExpanded(itemId) {
      return this.expandedItemIds.includes(itemId)
    },

    toggleItem(item) {
      const itemId = item.id
      if (this.isItemExpanded(itemId)) {
        this.expandedItemIds = this.expandedItemIds.filter((id) => id !== itemId)
        return
      }
      this.expandedItemIds = [...this.expandedItemIds, itemId]
      if (item.isManualTree) {
        this.loadResourceAssignmentActivities(item)
        return
      }
      this.loadResourceLinkedActivities(item)
    },

    isResourceLinksLoading(itemId) {
      return this.resourceLinksLoading.includes(itemId)
    },

    getResourceLinkDetail(item) {
      const detail = this.resourceLinkDetails[item.id]
      if (detail) {
        return detail
      }

      return {
        linkedActivities: [],
        totalReserved: item.totalReserved,
        availableQuantity: item.availableQuantity,
        loaded: false,
      }
    },

    mapLinkedActivityRow(activity, unit) {
      return {
        id: activity.id,
        name: this.activityLabel(activity),
        reserve: parseFloat(activity.quantity) || 0,
        assignedBySubs: 0,
        unit: activity.unit || unit,
        subsCount: 0,
      }
    },

    getResourceAssignmentDetail(item) {
      const detail = this.resourceAssignmentDetails[item.id]
      if (detail) {
        return detail
      }

      return {
        activities: [],
        loaded: false,
      }
    },

    mapAssignmentActivityRow(activity) {
      return {
        assignmentId: activity.assignment_id,
        activityId: activity.activity_id,
        isSubactivity: !!activity.is_subactivity,
        planningCode: activity.planning_code || null,
        name: activity.name || '',
        progress: parseFloat(activity.progress) || 0,
        manager: activity.manager || null,
        quantity: parseFloat(activity.quantity) || 0,
        unit: activity.unit || '',
        parent: activity.parent ? {
          activityId: activity.parent.activity_id,
          planningCode: activity.parent.planning_code || null,
          name: activity.parent.name || '',
          progress: parseFloat(activity.parent.progress) || 0,
        } : null,
      }
    },

    async loadResourceAssignmentActivities(item) {
      if (!item || !item.id || !item.isManualTree) {
        return
      }

      if (this.isResourceLinksLoading(item.id)) {
        return
      }

      this.resourceLinksLoading = [...this.resourceLinksLoading, item.id]

      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/boq-roots/${item.id}/assignment-activities`
        )

        if (data && data.success) {
          const activities = (data.activities || []).map((row) => this.mapAssignmentActivityRow(row))

          this.resourceAssignmentDetails = {
            ...this.resourceAssignmentDetails,
            [item.id]: {
              activities,
              loaded: true,
            },
          }
        }
      } catch (error) {
        this.$notify.error('error_loading_data')
      } finally {
        this.resourceLinksLoading = this.resourceLinksLoading.filter((id) => id !== item.id)
      }
    },

    async loadResourceLinkedActivities(item) {
      if (!item || !item.id || item.isManualTree) {
        return
      }

      if (this.isResourceLinksLoading(item.id)) {
        return
      }

      this.resourceLinksLoading = [...this.resourceLinksLoading, item.id]

      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/boq-roots/${item.id}/activities`
        )

        if (data && data.success) {
          const unit = item.unit || ''
          const linkedActivities = (data.activities || []).map((activity) => (
            this.mapLinkedActivityRow(activity, unit)
          ))

          this.resourceLinkDetails = {
            ...this.resourceLinkDetails,
            [item.id]: {
              linkedActivities,
              linkedActivityIds: linkedActivities.map((row) => row.id),
              totalReserved: parseFloat(data.total_reserved) || 0,
              availableQuantity: data.available_quantity,
              loaded: true,
            },
          }
        }
      } catch (error) {
        this.$notify.error('error_loading_data')
      } finally {
        this.resourceLinksLoading = this.resourceLinksLoading.filter((id) => id !== item.id)
      }
    },

    reloadExpandedResourceLinks(items) {
      if (!Array.isArray(items)) {
        return
      }

      items.forEach((item) => {
        if (!this.isItemExpanded(item.id)) {
          return
        }
        if (item.isManualTree) {
          this.loadResourceAssignmentActivities(item)
          return
        }
        this.loadResourceLinkedActivities(item)
      })
    },

    getItemLinkForm(itemId) {
      if (!this.itemLinkForms[itemId]) {
        this.itemLinkForms = {
          ...this.itemLinkForms,
          [itemId]: {
            activityId: null,
            activitySearch: '',
            quantity: null,
            remoteActivityOptions: null,
          },
        }
      }
      return this.itemLinkForms[itemId]
    },

    selectableActivities(item) {
      const detail = this.getResourceLinkDetail(item)
      const linkedIds = detail.linkedActivityIds || item.linkedActivityIds || []
      return this.parentActivities.filter((activity) => !linkedIds.includes(activity.id))
    },

    formatAvailableQuantity(item) {
      const detail = this.getResourceLinkDetail(item)
      const available = detail.loaded
        ? detail.availableQuantity
        : item.availableQuantity
      if (available == null) {
        return '—'
      }
      return this.formatQuantity(available)
    },

    canLinkActivity(item) {
      const form = this.getItemLinkForm(item.id)
      const quantity = parseFloat(form.quantity)
      const detail = this.getResourceLinkDetail(item)
      const available = detail.loaded
        ? detail.availableQuantity
        : item.availableQuantity
      if (!form.activityId || !Number.isFinite(quantity) || quantity <= 0) {
        return false
      }
      if (available == null) {
        return true
      }
      return quantity <= available + 0.0001
    },

    isLinkingActivity(itemId) {
      return this.linkingItemIds.includes(itemId)
    },

    unlinkKey(boqRootId, activityId) {
      return `${boqRootId}-${activityId}`
    },

    isUnlinkingActivity(boqRootId, activityId) {
      return this.unlinkingKeys.includes(this.unlinkKey(boqRootId, activityId))
    },

    linkButtonClass(item) {
      if (this.isLinkingActivity(item.id)) {
        return 'text-white bg-blue cursor-wait'
      }
      if (!this.canLinkActivity(item)) {
        return 'text-gray-400 bg-white border border-gray-300 cursor-not-allowed'
      }
      return 'text-white bg-blue cursor-pointer hover:bg-blue-dark'
    },

    async linkActivity(item) {
      const form = this.getItemLinkForm(item.id)
      const quantity = parseFloat(form.quantity)

      if (!form.activityId) {
        this.$notify.error('required_fields')
        return
      }
      if (!Number.isFinite(quantity) || quantity <= 0) {
        this.$notify.error('required_fields')
        return
      }
      const detail = this.getResourceLinkDetail(item)
      const available = detail.loaded
        ? detail.availableQuantity
        : item.availableQuantity
      if (available != null && quantity > available + 0.0001) {
        this.$notify.error(this.$t('boq_insufficient_quantity'))
        return
      }

      this.linkingItemIds = [...this.linkingItemIds, item.id]

      try {
        const { data } = await axios.post(
          `/api/v2/project/${this.projectId}/boq-roots/${item.id}/activities`,
          {
            activity_id: form.activityId,
            quantity,
          }
        )

        if (data && data.success) {
          this.$notify.success('save_successful')
          this.itemLinkForms = {
            ...this.itemLinkForms,
            [item.id]: {
              activityId: null,
              activitySearch: '',
              quantity: null,
              remoteActivityOptions: null,
            },
          }
          if (item.parentBoqRootId) {
            this.bumpTreeRefresh(item.parentBoqRootId)
          }
          await this.loadResourceLinkedActivities(item)
          return
        }

        const msg = data.message || 'assignment_change_fail'
        this.$notify.error(this.$t(msg))
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.message)
          || 'assignment_change_fail'
        this.$notify.error(this.$t(msg))
      } finally {
        this.linkingItemIds = this.linkingItemIds.filter((id) => id !== item.id)
      }
    },

    async unlinkActivity(item, linkedActivity) {
      const key = this.unlinkKey(item.id, linkedActivity.id)
      this.unlinkingKeys = [...this.unlinkingKeys, key]

      try {
        const { data } = await axios.delete(
          `/api/v2/project/${this.projectId}/boq-roots/${item.id}/activities/${linkedActivity.id}`
        )

        if (data && data.success) {
          this.$notify.success('save_successful')
          if (item.parentBoqRootId) {
            this.bumpTreeRefresh(item.parentBoqRootId)
          }
          await this.loadResourceLinkedActivities(item)
          return
        }

        const msg = data.message || 'assignment_change_fail'
        this.$notify.error(this.$t(msg))
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.message)
          || 'assignment_change_fail'
        this.$notify.error(this.$t(msg))
      } finally {
        this.unlinkingKeys = this.unlinkingKeys.filter((k) => k !== key)
      }
    },

    formatQuantity(value) {
      if (value == null || Number.isNaN(Number(value))) {
        return '—'
      }
      return Number(value).toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    formatCurrency(value) {
      if (value == null || Number.isNaN(Number(value))) {
        return '—'
      }
      return `${Number(value).toLocaleString('es-ES', { maximumFractionDigits: 0 })} €`
    },

    isAssignedOver(item) {
      return item.assigned != null && item.total != null && item.assigned > item.total
    },

    progressBarColor(item) {
      const progress = parseFloat(item.progress) || 0
      if (progress >= 100) {
        return 'red'
      }
      if (progress >= 70) {
        return 'green'
      }
      return 'blue-light'
    },
  },
}
</script>

<style scoped>
.boq-manual-row {
  background-color: #ede9fe;
}

.boq-manual-row:hover {
  background-color: #e4d9ff;
}

.boq-manual-row__chevron {
  color: #6d28d9;
}

.boq-manual-row__code-badge {
  background-color: #7c3aed;
}

.boq-manual-row__title {
  color: #5b21b6;
}

.boq-manual-row__meta {
  color: #7c3aed;
}

.boq-manual-row__action-btn {
  background-color: #7c3aed;
}

.boq-manual-row__action-btn:hover {
  background-color: #6d28d9;
}

.boq-manual-row__count {
  background-color: #5b21b6;
}

.boq-manual-row--nested {
  background-color: #f5f3ff;
}

.boq-manual-row--nested:hover {
  background-color: #ede9fe;
}

.boq-takeoff-row {
  background-color: #ecfdf5;
}

.boq-takeoff-row:hover {
  background-color: #d1fae5;
}

.boq-takeoff-row__chevron {
  color: #0f766e;
}

.boq-takeoff-row__code-badge {
  background-color: #0f766e;
}

.boq-takeoff-row__title {
  color: #115e59;
}

.boq-takeoff-row__meta {
  color: #0f766e;
}

.boq-takeoff-row__action-btn {
  background-color: #0f766e;
}

.boq-takeoff-row__action-btn:hover {
  background-color: #0d9488;
}

.boq-takeoff-row__action-btn--danger {
  background-color: #b91c1c;
}

.boq-takeoff-row__action-btn--danger:hover {
  background-color: #dc2626;
}

.boq-takeoff-row__count {
  background-color: #115e59;
}

.boq-takeoff-row--nested {
  background-color: #f0fdfa;
}

.boq-takeoff-row--nested:hover {
  background-color: #ecfdf5;
}

.boq-takeoff-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.85rem;
  border: 0;
  border-radius: 0.25rem;
  background: #0f766e;
  color: #fff;
  cursor: pointer;
  line-height: 1.1;
}

.boq-takeoff-btn:hover {
  background: #0d9488;
}

.boq-takeoff-btn__label {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
</style>
